import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, MessageCircle, MapPin, Plus, Send, MessageSquare, CornerDownRight, Trash2, Search, X as XIcon, Flag } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionRibbon } from "@/components/AppShell";
import UploadPhotoButton from "@/components/UploadPhotoButton";
import PhotoCarousel from "@/components/PhotoCarousel";
import RowMenu from "@/components/RowMenu";
import UserActionsMenu from "@/components/UserActionsMenu";
import LoungeTermsDialog from "@/components/LoungeTermsDialog";
import { addLocalBookmark } from "@/lib/bookmarks";
import { LOUNGE } from "@/constants/testIds";
import { toast } from "sonner";
import useUserSearch from "@/hooks/useUserSearch";
import UserMatchCard from "@/components/UserMatchCard";
import { useIsAdmin } from "@/components/ReviewerLockdown";

// Item 2 — Small "People" pane rendered above the story feed on the
// Lounge page whenever the search query matches any users.
function LoungePeopleResults({ query }) {
  const { users } = useUserSearch(query, { enabled: !!query.trim(), limit: 5 });
  if (!query.trim() || users.length === 0) return null;
  return (
    <div className="mt-3" data-testid="lounge-people-section">
      <div className="mb-1.5 px-0.5">
        <span className="font-hand text-tb-navy/70 text-xs uppercase tracking-wide">People</span>
      </div>
      <ul className="space-y-2">
        {users.map((u) => (
          <li key={u.id}><UserMatchCard user={u} /></li>
        ))}
      </ul>
    </div>
  );
}

function HashtagPill({ tag }) {
  const palette = [
    "bg-tb-coral-soft text-tb-coral",
    "bg-tb-blue-soft text-tb-blue",
    "bg-tb-orange-soft text-tb-orange",
    "bg-tb-purple-soft text-tb-purple",
    "bg-tb-cream-2 text-tb-navy",
  ];
  const idx = Math.abs(tag.length * 11) % palette.length;
  return (
    <span className={`text-xs font-hand px-2.5 py-1 rounded-full ${palette[idx]}`}>
      # {tag}
    </span>
  );
}

function CommentsBlock({ postId, open, currentUserId, onCommentRemoved, onCommentRestored }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [loading, setLoading] = useState(false);
  // When set, the next submit posts as a reply to this comment id.
  const [replyTo, setReplyTo] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/lounge/posts/${postId}/comments`);
      setComments(data.comments || []);
    } catch (err) {
      console.error("[LoungePage] failed to load comments:", err);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);
  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPosting(true);
    try {
      const payload = { text };
      if (replyTo) payload.parent_id = replyTo;
      const { data } = await api.post(`/lounge/posts/${postId}/comments`, payload);
      setComments((c) => [...c, data]);
      setText("");
      setReplyTo(null);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "lounge_terms_required") {
        toast.info("Tap 'new story' once to read & accept our community policy — then you can comment.");
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally { setPosting(false); }
  };

  // Build a tree: top-level comments + their direct replies.
  const topLevel = comments.filter((c) => !c.parent_id);
  const repliesByParent = comments.reduce((acc, c) => {
    if (c.parent_id) (acc[c.parent_id] = acc[c.parent_id] || []).push(c);
    return acc;
  }, {});

  const removeComment = (c) => {
    // Snapshot the comment + its replies so we can restore on undo.
    const replies = comments.filter((x) => x.parent_id === c.id);
    const snapshot = [c, ...replies];
    const removedCount = snapshot.length;
    // Optimistically remove from view.
    setComments((cs) => cs.filter((x) => x.id !== c.id && x.parent_id !== c.id));
    for (let i = 0; i < removedCount; i += 1) onCommentRemoved?.();

    let undone = false;
    const restore = () => {
      undone = true;
      setComments((cs) => [...cs, ...snapshot]);
      for (let i = 0; i < removedCount; i += 1) onCommentRestored?.();
    };
    const timer = setTimeout(async () => {
      if (undone) return;
      try {
        await api.delete(`/lounge/comments/${c.id}`);
      } catch (err) {
        restore();
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't delete");
      }
    }, 5000);

    toast(
      removedCount > 1
        ? `Comment + ${removedCount - 1} repl${removedCount - 1 === 1 ? "y" : "ies"} deleted`
        : "Comment deleted",
      {
        duration: 5000,
        action: {
          label: "Undo",
          onClick: () => { clearTimeout(timer); restore(); },
        },
      }
    );
  };

  const renderComment = (c, depth = 0) => (
    <div key={c.id} className={"flex items-start gap-2 " + (depth ? "mt-1.5" : "")}>
      <UserActionsMenu
        targetUserId={c.user_id}
        targetName={c.author_name}
        targetAvatar={c.author_avatar}
        currentUserId={currentUserId}
        contextLabel="comment"
        contextType="comment"
        contextId={c.id}
        onBlocked={() => {
          // Hide every comment by this user locally after blocking.
          setComments((cs) => cs.filter((x) => x.user_id !== c.user_id));
          onCommentRemoved?.();
        }}
        imgClassName="w-7 h-7 rounded-full bg-tb-cream-2 object-cover shrink-0"
        testIdPrefix={`lounge-comment-avatar-${c.id}`}
      />
      <div className="flex-1 min-w-0">
        <div className="bg-tb-cream-2 rounded-xl px-3 py-1.5 max-w-[92%] inline-block">
          <div className="font-ui font-semibold text-tb-navy text-xs">{c.author_name}</div>
          <div className="font-ui text-sm text-tb-navy/85 whitespace-pre-wrap break-words">{c.text}</div>
        </div>
        <div className="flex items-center gap-3 mt-0.5 ml-2">
          <button
            onClick={() => setReplyTo(c.id)}
            className="font-hand text-xs text-tb-blue hover:text-tb-coral"
            data-testid={`lounge-comment-reply-${c.id}`}
          >
            reply
          </button>
          {c.user_id === currentUserId && (
            <button
              onClick={() => removeComment(c)}
              data-testid={`lounge-comment-delete-${c.id}`}
              className="flex items-center gap-1 font-hand text-xs text-tb-coral hover:text-red-700 tb-tap"
              title="Delete comment"
            >
              <Trash2 className="w-3 h-3" /> delete
            </button>
          )}
        </div>
        {(repliesByParent[c.id] || []).map((r) => (
          <div key={r.id} className="mt-1.5 ml-4 border-l-2 border-tb-blue/30 pl-2">
            {renderComment(r, depth + 1)}
          </div>
        ))}
      </div>
    </div>
  );

  const replyingComment = replyTo ? comments.find((c) => c.id === replyTo) : null;

  return (
    <div className="mt-3 pl-2 border-l-2 border-tb-blue/40">
      {loading && <div className="font-hand text-tb-navy/50">~ loading ~</div>}
      <div className="space-y-2">
        {topLevel.length === 0 && !loading && (
          <div className="font-hand text-tb-navy/50 text-sm">be the first to ask a question…</div>
        )}
        {topLevel.map((c) => renderComment(c))}
      </div>

      {replyingComment && (
        <div className="flex items-center justify-between mt-2 px-2 py-1 rounded-lg bg-tb-blue-soft border border-tb-blue/40">
          <span className="font-hand text-xs text-tb-blue truncate flex items-center gap-1">
            <CornerDownRight className="w-3 h-3" />
            replying to <b className="ml-1">{replyingComment.author_name}</b>
          </span>
          <button
            type="button"
            onClick={() => setReplyTo(null)}
            className="font-hand text-xs text-tb-coral tb-tap"
          >
            cancel
          </button>
        </div>
      )}

      <form onSubmit={submit} className="flex items-center gap-2 mt-2">
        <input
          value={text} onChange={(e) => setText(e.target.value)}
          placeholder={replyTo ? "write your reply…" : "comment on the story…"}
          className="flex-1 px-3 py-2 rounded-full bg-white border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm" />
        <button disabled={posting}
          className="w-9 h-9 rounded-full bg-tb-navy text-tb-gold flex items-center justify-center tb-tap disabled:opacity-50">
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}

function PostCard({ p, idx, onLike, currentUserId, onPostHidden, onPostDeleted, onPostRestored, onPostEdit, onCommentRemoved, onCommentRestored, onFollowChange }) {
  const [showComments, setShowComments] = useState(false);
  const nav = useNavigate();
  const isOwn = p.user_id === currentUserId;
  const isAdmin = useIsAdmin();
  // 2026-07-17 Fix 1 — Follow button state. The feed endpoint ships
  // `is_followed` on every post so we don't need an extra call for
  // the initial paint; only mutate on user tap.
  const [following, setFollowing] = useState(!!p.is_followed);
  const [followBusy, setFollowBusy] = useState(false);
  React.useEffect(() => { setFollowing(!!p.is_followed); }, [p.is_followed]);

  const toggleFollow = async () => {
    if (followBusy || isOwn) return;
    setFollowBusy(true);
    const next = !following;
    setFollowing(next); // optimistic
    try {
      if (next) await api.post(`/users/${p.user_id}/follow`);
      else      await api.delete(`/users/${p.user_id}/follow`);
      onFollowChange?.(p.user_id, next);
    } catch (e) {
      setFollowing(!next); // rollback
      console.error("[LoungePage] follow toggle failed:", e?.response?.data?.detail || e);
    } finally { setFollowBusy(false); }
  };

  const savePostAsBookmark = () => {
    addLocalBookmark({
      id: `lounge:post:${p.id}`,
      name: (p.text || "").slice(0, 60) || "Lounge post",
      title: (p.text || "").slice(0, 60) || "Lounge post",
      subtitle: p.author_name || "",
      images: p.photos?.length ? p.photos : p.image ? [p.image] : [],
      image: p.photos?.[0] || p.image || "",
      provider: "lounge",
      vibe: "",
      description: p.text || "",
      location: { city: p.location || "" },
    });
    // 2026-07-15 #polish2 — removed "Post saved to bookmarks" toast;
    // the bookmark icon visually flips.
  };

  const removePost = () => {
    const snapshot = { ...p };
    onPostDeleted?.(p.id);

    let undone = false;
    const restore = () => {
      undone = true;
      onPostRestored?.(snapshot, idx);
    };
    const timer = setTimeout(async () => {
      if (undone) return;
      try {
        await api.delete(`/lounge/posts/${p.id}`);
      } catch (err) {
        restore();
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't delete");
      }
    }, 5000);

    toast("Story deleted", {
      duration: 5000,
      action: {
        label: "Undo",
        onClick: () => { clearTimeout(timer); restore(); },
      },
    });
  };
  const adminForceDelete = async () => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `Delete this post by @${p.author_handle || p.author_name || "user"}?\n` +
      `This bypasses the author-only check because you are an admin. Cannot be undone.`,
    );
    if (!ok) return;
    const snapshot = { ...p };
    onPostDeleted?.(p.id);
    try {
      await api.delete(`/lounge/posts/${p.id}`);
      toast.success("Post deleted (admin override)");
    } catch (err) {
      onPostRestored?.(snapshot, idx);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't delete");
    }
  };

  return (
    <article data-testid={LOUNGE.post(p.id)}
      className="border-b border-tb-cream-3/80 pb-6 pt-4">
      <div className="flex items-center justify-between mb-2 px-1">
        <span className="font-hand text-tb-blue text-base italic">
          Story {String(idx + 1).padStart(2, "0")} ✦ {timeAgo(p.created_at)}
        </span>
        {isAdmin && !isOwn && (
          <button
            type="button"
            data-testid={`lounge-admin-delete-${p.id}`}
            onClick={adminForceDelete}
            title="Admin: delete this post"
            className="text-tb-navy/40 hover:text-tb-coral tb-tap p-1"
            aria-label="Admin delete post"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="flex items-start gap-3">
        <UserActionsMenu
          targetUserId={p.user_id}
          targetName={p.author_name}
          targetAvatar={p.author_avatar}
          currentUserId={currentUserId}
          contextLabel="post"
          contextType="post"
          contextId={p.id}
          onBlocked={() => onPostHidden?.(p.user_id)}
          imgClassName="w-12 h-12 rounded-full bg-tb-cream-2 object-cover shrink-0"
          testIdPrefix={`lounge-post-avatar-${p.id}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <div className="font-serif-it text-tb-navy text-xl font-semibold">{p.author_name}</div>
            <span className="font-ui text-sm text-tb-navy/60">@{p.author_handle}</span>
          </div>
          <div className="font-hand text-tb-navy/70 text-sm flex items-center gap-1 mt-0.5">
            <MapPin className="w-3.5 h-3.5" /> {p.location}
          </div>
        </div>
        {isOwn ? (
          <RowMenu
            testId={`lounge-post-menu-${p.id}`}
            onEdit={() => onPostEdit?.(p)}
            onSave={savePostAsBookmark}
            onDelete={removePost}
          />
        ) : (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              data-testid={`lounge-follow-${p.user_id}`}
              onClick={toggleFollow}
              disabled={followBusy}
              aria-pressed={following}
              className={
                "px-3 py-1 rounded-full font-hand text-xs tb-tap border-2 transition-colors disabled:opacity-60 " +
                (following
                  ? "bg-white text-tb-navy border-tb-navy hover:bg-tb-navy/5"
                  : "bg-tb-navy text-white border-tb-navy hover:bg-tb-navy-deep")
              }
            >
              {following ? "Following" : "Follow"}
            </button>
            <button
              data-testid={`lounge-message-${p.user_id}`}
              onClick={() => nav(`/messages?with=${p.user_id}`)}
              className="flex items-center gap-1 px-3 py-1 rounded-full bg-tb-blue text-white border border-tb-blue font-hand text-sm tb-tap hover:brightness-110"
              title={`Message ${p.author_name}`}
            >
              <MessageSquare className="w-3.5 h-3.5" /> message
            </button>
            <RowMenu
              testId={`lounge-post-menu-${p.id}`}
              onSave={savePostAsBookmark}
            />
          </div>
        )}
      </div>

      {(p.photos && p.photos.length > 0) ? (
        <div className="mt-3">
          <PhotoCarousel photos={p.photos} testId={`lounge-carousel-${p.id}`} />
        </div>
      ) : p.image && (
        <div className="mt-3 rounded-2xl overflow-hidden border-2 border-tb-cream-3">
          <img src={p.image} alt="" className="w-full max-h-72 object-cover" />
        </div>
      )}

      <p className="mt-3 font-serif-it text-tb-navy text-lg leading-snug">
        "{p.text}"
      </p>

      {p.hashtags?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {p.hashtags.map((t) => <HashtagPill key={t} tag={t} />)}
        </div>
      )}

      <div className="mt-3 flex items-center gap-5">
        <button
          data-testid={LOUNGE.likeBtn(p.id)}
          onClick={() => onLike(p)}
          className="flex items-center gap-1 font-hand text-tb-navy/80 tb-tap"
        >
          <Heart className={`w-5 h-5 ${p.liked_by_me ? "text-tb-coral" : "text-tb-navy/60"}`}
            fill={p.liked_by_me ? "currentColor" : "none"} />
          {p.likes}
        </button>
        <button
          data-testid={LOUNGE.commentBtn(p.id)}
          onClick={() => setShowComments((s) => !s)}
          className="flex items-center gap-1 font-hand text-tb-navy/80 tb-tap"
        >
          <MessageCircle className="w-5 h-5" /> {p.comments_count}
        </button>
        <button
          data-testid={LOUNGE.reportBtn(p.id)}
          onClick={async () => {
            // Bug 25: inline Report button. Uses window.prompt for the
            // reason (same UX pattern as UserActionsMenu.jsx) so no new
            // modal state is introduced. POSTs to /reports which
            // already notifies admins for review.
            const reason = window.prompt(
              "Report this post for admin review? (optional details — e.g. spam, harassment, off-topic)"
            );
            if (reason === null) return; // cancelled
            try {
              await api.post("/reports", {
                target_type: "lounge_post",
                target_id: p.id,
                target_user_id: p.author_id || p.user_id || null,
                reason: reason || null,
              });
              toast.success("Reported — thanks, we'll review it shortly");
            } catch (e) {
              toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't report");
            }
          }}
          className="flex items-center gap-1 font-hand text-tb-navy/60 hover:text-tb-coral tb-tap ml-auto"
          title="Report post"
        >
          <Flag className="w-4 h-4" />
        </button>
      </div>
      <CommentsBlock postId={p.id} open={showComments} currentUserId={currentUserId} onCommentRemoved={onCommentRemoved} onCommentRestored={onCommentRestored} />
    </article>
  );
}

function timeAgo(iso) {
  if (!iso) return "just now";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function PostDialog({ onClose, onSaved, initial }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    location: initial?.location || "",
    photos: initial?.photos || [],
    text: initial?.text || "",
    hashtags: (initial?.hashtags || []).join(", "),
  });
  const [posting, setPosting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setPosting(true);
    try {
      const tags = form.hashtags.split(",").map((s) => s.trim().replace(/^#/, "")).filter(Boolean);
      const payload = {
        location: form.location,
        text: form.text,
        hashtags: tags,
        photos: form.photos,
        image: form.photos?.[0] || "",
      };
      const { data } = isEdit
        ? await api.put(`/lounge/posts/${initial.id}`, payload)
        : await api.post("/lounge/posts", payload);
      // 2026-07-15 #polish2 — removed "story shared/updated" toast;
      // the post appears (or updates) inline in the Lounge feed.
      onSaved(data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setPosting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-tb-navy-deep/70 flex items-end md:items-center justify-center p-3">
      <form onSubmit={submit}
        role="dialog" aria-modal="true" aria-labelledby="post-dialog-title"
        className="bg-white rounded-2xl border border-tb-navy/15 max-w-md w-full max-h-[92vh] overflow-y-auto shadow-[0_20px_50px_-16px_rgba(26,58,95,0.35)]">
        <div className="bg-tb-navy text-white px-4 py-3 flex items-center justify-between rounded-t-2xl">
          <span id="post-dialog-title" className="font-hand text-xl">{isEdit ? "edit story" : "share a story"}</span>
          <button type="button" onClick={onClose} className="font-hand text-white/80 hover:text-white tb-tap">close</button>
        </div>
        <div className="p-4 space-y-3">
          <input
            data-testid={LOUNGE.newPostLocation}
            required value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            placeholder="Where? (e.g. Lisbon, Portugal)"
            className="w-full px-3 py-2 rounded-lg bg-white border border-tb-navy/20 focus:border-tb-navy outline-none text-tb-navy" />
          <UploadPhotoButton
            testId={LOUNGE.newPostImage}
            value={form.photos}
            onChange={(v) => setForm({ ...form, photos: v })}
            max={5}
            overflowMessage="More storage options coming soon!"
            label={form.photos.length ? `Add more (${form.photos.length}/5)` : "Upload photos (up to 5)"}
          />
          <textarea
            data-testid={LOUNGE.newPostText}
            required rows={4} value={form.text}
            onChange={(e) => setForm({ ...form, text: e.target.value })}
            placeholder="your travel story…"
            className="w-full px-3 py-2 rounded-lg bg-white border border-tb-navy/20 focus:border-tb-navy outline-none text-tb-navy" />
          <input value={form.hashtags}
            onChange={(e) => setForm({ ...form, hashtags: e.target.value })}
            placeholder="hashtags, comma separated (e.g. Italy, HiddenGems)"
            className="w-full px-3 py-2 rounded-lg bg-white border border-tb-navy/20 focus:border-tb-navy outline-none text-tb-navy" />
          <button
            data-testid={isEdit ? "lounge-edit-post-submit" : LOUNGE.newPostSubmit}
            disabled={posting} type="submit"
            className="w-full py-3 rounded-xl bg-tb-navy text-tb-gold-bright font-hand text-xl border-2 border-tb-gold tb-tap disabled:opacity-60">
            {posting ? (isEdit ? "saving…" : "posting…") : (isEdit ? "save changes" : "post story ✨")}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function LoungePage() {
  const { user, refresh } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [query, setQuery] = useState("");
  // 2026-07-17 Fix 1 — feed filter pills. Server-side filter drives
  // the fetch; default "for_you" gives a personalized mix for new
  // + returning users. Pill order matches the spec.
  const [feedFilter, setFeedFilter] = useState("for_you");
  const [showTerms, setShowTerms] = useState(false);
  const needsTerms = user && !user.accepted_lounge_terms_at;

  const handleNewStoryClick = () => {
    if (needsTerms) setShowTerms(true);
    else setComposing(true);
  };

  const load = async (qval = "", filterVal = feedFilter) => {
    setLoading(true);
    try {
      const params = {};
      // Item 2 — `?user_id=X` on the lounge URL filters the feed to
      // that user's posts. Set by the See-lounge-posts CTA on the
      // UserMatchCard so `/lounge?user_id=abc` renders a filtered feed.
      const filterUserId = new URLSearchParams(window.location.search).get("user_id");
      if (filterUserId) {
        params.user_id = filterUserId;
      } else if (qval) {
        params.q = qval;
      } else if (filterVal) {
        params.filter = filterVal;
      }
      const { data } = await api.get("/lounge/posts", { params });
      setPosts(data.posts || []);
    } catch (e) {
      console.error("[LoungePage] load failed:", e?.response?.data?.detail || e);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce search input so we don't hammer the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => { load(query.trim(), feedFilter); }, 250);
    return () => clearTimeout(t);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload the feed when the filter pill selection changes.
  useEffect(() => {
    if (query.trim()) return; // search query wins over filter
    load("", feedFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedFilter]);

  const onLike = async (p) => {
    try {
      const { data } = await api.post(`/lounge/posts/${p.id}/like`);
      setPosts((prev) => prev.map((x) =>
        x.id === p.id
          ? { ...x, liked_by_me: data.liked, likes: x.likes + (data.liked ? 1 : -1) }
          : x
      ));
    } catch (err) {
      console.error("[LoungePage] like toggle failed:", err);
    }
  };

  return (
    <div data-testid={LOUNGE.page} className="flex flex-col">
      <SectionRibbon>the lounge</SectionRibbon>
      <span data-tour="lounge-header" className="block" style={{position: "absolute", pointerEvents: "none", height: 1, width: 1}} aria-hidden="true" />

      <div className="px-5 py-4 flex items-center justify-between">
        <p className="font-hand text-tb-navy/70 italic text-base">
          stories from the trail — share yours
        </p>
        <button
          data-testid={LOUNGE.newPostBtn}
          onClick={handleNewStoryClick}
          className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-base tb-tap"
        >
          <Plus className="w-4 h-4" /> new story
        </button>
      </div>

      {/* Search by @user, #hashtag, or any name/handle */}
      <div className="px-5 pb-3">
        <div className="relative">
          <Search className="w-4 h-4 text-tb-navy/45 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            data-testid="lounge-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="search #hashtag or @user…"
            className="w-full pl-9 pr-9 py-2 rounded-full bg-white border-2 border-tb-navy/15 focus:border-tb-navy outline-none font-ui text-sm"
          />
          {query && (
            <button
              type="button"
              data-testid="lounge-search-clear"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-tb-cream-3 text-tb-navy/70 flex items-center justify-center tb-tap"
              aria-label="Clear search"
            >
              <XIcon className="w-3 h-3" />
            </button>
          )}
        </div>
        {query.trim() && !loading && (
          <p className="font-hand text-tb-navy/60 italic text-xs mt-1.5">
            {posts.length} {posts.length === 1 ? "story" : "stories"} for <b>{query.trim()}</b>
          </p>
        )}
        {/* Item 2 — People matches surface above the story feed when
            the query looks like a person search. */}
        <LoungePeopleResults query={query} />

        {/* 2026-07-17 Fix 1 — feed filter pills. Hidden when the user
            is searching or has landed via `?user_id=…` since those
            paths override server-side filtering entirely.
            2026-07-17 Fix 2 (v2) — 2x pill size + leading-normal +
            centered row so descenders in "Following" don't clip. */}
        {!query.trim() && !new URLSearchParams(window.location.search).get("user_id") && (
          <div
            data-testid="lounge-filter-pills"
            className="mt-4 flex items-center justify-center gap-3 md:gap-4 overflow-x-auto"
            role="tablist"
            aria-label="Feed filter"
          >
            {[
              { id: "for_you",   label: "For You" },
              { id: "following", label: "Following" },
              { id: "recent",    label: "Recent" },
              { id: "popular",   label: "Popular" },
            ].map((f) => {
              const active = feedFilter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  data-testid={`lounge-filter-${f.id}`}
                  onClick={() => setFeedFilter(f.id)}
                  className={
                    "px-8 md:px-10 py-3 md:py-4 rounded-full font-hand whitespace-nowrap tb-tap border-2 transition-colors " +
                    "text-base md:text-lg leading-normal " +
                    (active
                      ? "bg-tb-navy text-white border-tb-navy"
                      : "bg-white text-tb-navy border-tb-navy/25 hover:border-tb-navy")
                  }
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="px-5 pb-8 max-w-2xl mx-auto w-full">
        {loading ? (
          <div className="font-hand text-tb-navy/60 text-xl text-center py-12">
            ~ tuning the lounge speakers ~
          </div>
        ) : posts.length === 0 ? (
          feedFilter === "following" && !query.trim() ? (
            <div
              data-testid="lounge-following-empty"
              className="mx-auto max-w-md rounded-2xl border-2 border-dashed border-tb-navy/25 bg-white text-center py-10 px-6"
            >
              <p className="font-serif-it italic text-tb-navy text-xl">
                Follow travelers to fill this feed
              </p>
              <p className="mt-1.5 font-hand text-tb-navy/55 text-sm">
                Tap Follow on any post to start.
              </p>
              <button
                type="button"
                data-testid="lounge-following-empty-cta"
                onClick={() => setFeedFilter("recent")}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-tb-navy text-white border-2 border-tb-navy hover:bg-tb-navy-deep font-hand text-sm tb-tap"
              >
                Explore travelers
              </button>
            </div>
          ) : (
            <div className="font-hand text-tb-navy/60 text-lg text-center py-12">
              No posts found
            </div>
          )
        ) : (
          posts.map((p, i) => (
            <PostCard
              key={p.id} p={p} idx={i} onLike={onLike} currentUserId={user?.id}
              onFollowChange={(userId, isFollowed) => {
                // Reflect follow-state change across every post from
                // the same author so all buttons flip in one paint.
                setPosts((prev) => prev.map((x) =>
                  x.user_id === userId ? { ...x, is_followed: isFollowed } : x
                ));
              }}
              onPostHidden={(blockedUserId) => {
                // Locally hide every post by the blocked user — backend already
                // filters going forward.
                setPosts((prev) => prev.filter((x) => x.user_id !== blockedUserId));
              }}
              onPostDeleted={(postId) => {
                setPosts((prev) => prev.filter((x) => x.id !== postId));
              }}
              onPostRestored={(post, originalIdx) => {
                setPosts((prev) => {
                  if (prev.some((x) => x.id === post.id)) return prev;
                  const next = [...prev];
                  next.splice(Math.min(originalIdx, next.length), 0, post);
                  return next;
                });
              }}
              onPostEdit={(post) => setEditingPost(post)}
              onCommentRemoved={() => {
                setPosts((prev) => prev.map((x) =>
                  x.id === p.id
                    ? { ...x, comments_count: Math.max(0, (x.comments_count || 0) - 1) }
                    : x
                ));
              }}
              onCommentRestored={() => {
                setPosts((prev) => prev.map((x) =>
                  x.id === p.id
                    ? { ...x, comments_count: (x.comments_count || 0) + 1 }
                    : x
                ));
              }}
            />
          ))
        )}
      </div>

      {composing && (
        <PostDialog
          onClose={() => setComposing(false)}
          onSaved={(p) => setPosts((prev) => [p, ...prev])}
        />
      )}
      {editingPost && (
        <PostDialog
          initial={editingPost}
          onClose={() => setEditingPost(null)}
          onSaved={(updated) => setPosts((prev) =>
            prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x))
          )}
        />
      )}
      {showTerms && (
        <LoungeTermsDialog
          onClose={() => setShowTerms(false)}
          onAgree={async () => {
            setShowTerms(false);
            await refresh?.();
            setComposing(true);
          }}
        />
      )}
    </div>
  );
}
